import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search, Check } from 'lucide-react';
import BottomSheet from './BottomSheet';
import useIsNarrowViewport from './useIsNarrowViewport';
import './ui-components.css';

/**
 * CustomSelect — replaces native <select>
 *
 * Props:
 *   value      — current value
 *   onChange   — (value) => void
 *   options    — [{ value, label, emoji?, icon? }]
 *   placeholder — string
 *   searchable — boolean (default: options.length > 5)
 *
 * Below the app's mobile breakpoint (max-width: 767px — the same one
 * Layout/AppShell.css switches chrome on) the options render in a BottomSheet
 * instead of the floating `.custom-select__dropdown` popover: a desktop-sized
 * popover under a fat-finger tap is small, easy to mis-tap, and easy to
 * dismiss by accident. The option markup itself (search box + option list)
 * is identical either way — only the container changes.
 */
export default function CustomSelect({
    value,
    onChange,
    options = [],
    placeholder = 'Select...',
    searchable,
    className = '',
    style = {},
    id,
    ariaLabel,
}) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const ref = useRef(null);
    const searchRef = useRef(null);
    const triggerRef = useRef(null);

    const isNarrow = useIsNarrowViewport();
    const showSearch = searchable ?? options.length > 5;

    // The BottomSheet path owns its own dismissal (backdrop tap, Escape, drag,
    // close button) and lives in a portal outside `ref.current`'s subtree —
    // this legacy "click outside the popover" listener is for the classic
    // dropdown only, and would otherwise mis-fire on every tap inside the
    // portaled sheet (its DOM isn't a descendant of `ref.current` any more).
    useEffect(() => {
        if (isNarrow) return undefined;
        const handleClick = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        if (open) document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [open, isNarrow]);

    useEffect(() => {
        if (open && showSearch && searchRef.current) {
            searchRef.current.focus();
        }
    }, [open, showSearch]);

    const selectedOption = options.find(o => o.value === value);

    const filtered = search.trim()
        ? options.filter(o =>
            o.label.toLowerCase().includes(search.toLowerCase()) ||
            (o.emoji && o.emoji.includes(search))
        )
        : options;

    const handleSelect = (val) => {
        onChange(val);
        setOpen(false);
        setSearch('');
    };

    // Backdrop tap / Escape / drag-dismiss / close button on the sheet path —
    // no selection made, just close.
    const handleClose = () => {
        setOpen(false);
        setSearch('');
    };

    // Shared between both containers so the popover and the sheet can never
    // drift into two different implementations of "the options list".
    const optionsContent = (
        <>
            {showSearch && (
                <div className="custom-select__search">
                    <Search size={14} />
                    <input
                        ref={searchRef}
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Search..."
                    />
                </div>
            )}
            <div className="custom-select__options">
                {filtered.map(option => (
                    <button
                        key={option.value}
                        type="button"
                        className={`custom-select__option ${value === option.value ? 'custom-select__option--selected' : ''}`}
                        onClick={() => handleSelect(option.value)}
                    >
                        {option.icon && (
                            <span className="custom-select__option-icon" style={{ display: 'inline-flex', alignItems: 'center', marginRight: 'var(--space-2xs)' }}>
                                {option.icon}
                            </span>
                        )}
                        {option.emoji && (
                            <span className="custom-select__option-emoji">{option.emoji}</span>
                        )}
                        <span>{option.label}</span>
                        {value === option.value && (
                            <Check size={14} className="custom-select__option-check" style={{ marginLeft: 'auto' }} />
                        )}
                    </button>
                ))}
                {filtered.length === 0 && (
                    <div className="custom-select__empty">No options found</div>
                )}
            </div>
        </>
    );

    return (
        <div className={`custom-select ${className}`} style={style} ref={ref}>
            <button
                type="button"
                id={id}
                ref={triggerRef}
                aria-label={ariaLabel}
                aria-haspopup="listbox"
                aria-expanded={open}
                className={[
                    'btn btn-tertiary custom-select__trigger',
                    open && 'custom-select__trigger--open',
                    !selectedOption && 'custom-select__trigger--placeholder',
                ].filter(Boolean).join(' ')}
                onClick={() => setOpen(!open)}
            >
                {selectedOption?.icon && (
                    <span className="custom-select__option-icon" style={{ display: 'inline-flex', alignItems: 'center', marginRight: 'var(--space-2xs)' }}>
                        {selectedOption.icon}
                    </span>
                )}
                {selectedOption?.emoji && (
                    <span className="custom-select__option-emoji">{selectedOption.emoji}</span>
                )}
                <span className="custom-select__trigger-text">
                    {selectedOption ? selectedOption.label : placeholder}
                </span>
                <ChevronDown size={14} className="custom-select__trigger-icon" style={{ marginLeft: 'auto' }} />
            </button>

            {isNarrow ? (
                <BottomSheet
                    isOpen={open}
                    onClose={handleClose}
                    title={ariaLabel || placeholder}
                    initialFocusRef={showSearch ? searchRef : undefined}
                    restoreFocusRef={triggerRef}
                    className="custom-select__sheet"
                >
                    {optionsContent}
                </BottomSheet>
            ) : (
                open && (
                    <div className="custom-select__dropdown">
                        {optionsContent}
                    </div>
                )
            )}
        </div>
    );
}
